import { useState } from 'react';
import { api } from '../../api/client.js';
import { useSession } from '../../api/session.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { tr, trf } from '../../lib/i18n';
import { telechargeLaSauvegarde } from '../../lib/sauvegarde';
import { C } from '../../theme/palette';

/** Ce que le patron peut effacer, dans l'ordre de l'écran. */
const SORTES = [
  ['chantiers', '🏗 Chantiers', 'avec leurs heures, rapports, photos, plans et achats'],
  ['offres', '📄 Offres', 'et les consultations de tes clients'],
  ['factures', '🧾 Factures', 'payées ou non'],
  ['depenses', '💸 Dépenses', ''],
];

/** Le mot à écrire pour confirmer. */
const MOT_DE_CONFIRMATION = 'EFFACER';

/**
 * Réglages → Mon compte → « Repartir à zéro » : le patron qui a fait des
 * essais efface chantiers, offres, factures et dépenses avant de se servir de
 * SwissTech pour de vrai. Les clients, les employés, le catalogue et les
 * réglages restent. Il coche ce qui part et écrit « EFFACER » pour confirmer.
 *
 * @returns {import('react').ReactElement|null} la carte, au patron seulement
 */
export function RepartirAZeroCard() {
  const { session } = useSession();
  const [ouvert, setOuvert] = useState(false);
  const [choix, setChoix] = useState({
    chantiers: true,
    offres: true,
    factures: true,
    depenses: true,
  });
  const [mot, setMot] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  if (session?.role !== 'patron') return null;

  const rien = !Object.values(choix).some(Boolean);
  const pret = !rien && mot.trim().toUpperCase() === MOT_DE_CONFIRMATION && !enCours;

  const efface = async () => {
    setEnCours(true);
    setErreur('');
    try {
      const bilan = await api.post('/societe/remise-a-zero', {
        ...choix,
        confirmation: MOT_DE_CONFIRMATION,
      });
      window.alert(
        trf(
          '✓ Effacé : {0} chantier(s), {1} offre(s), {2} facture(s), {3} dépense(s).',
          bilan?.chantiers ?? 0,
          bilan?.offres ?? 0,
          bilan?.factures ?? 0,
          bilan?.depenses ?? 0,
        ),
      );
      // Toutes les listes repartent du serveur, vides.
      window.location.reload();
    } catch (e) {
      setErreur(e?.message || tr('L’effacement a échoué.'));
      setEnCours(false);
    }
  };

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="🧹"
        title={tr('Repartir à zéro')}
        color={C.red}
        sub={tr('Effacer tes essais avant de commencer pour de vrai')}
      />
      <div style={{ padding: 13, fontSize: 11.5, lineHeight: 1.5, color: C.ink }}>
        <div style={{ marginBottom: 10 }}>
          {tr(
            'Les clients, les employés, le catalogue et les réglages restent. Les numéros d’offre et de facture continuent.',
          )}
        </div>
        {!ouvert && (
          <Btn
            label="🧹 Repartir à zéro"
            ghost
            color={C.red}
            full
            onClick={() => {
              setOuvert(true);
              setMot('');
              setErreur('');
            }}
          />
        )}
        {ouvert && (
          <div style={{ border: '1px solid ' + C.red, borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontWeight: 800, color: C.red, marginBottom: 6 }}>
              {tr('Que faut-il effacer ?')}
            </div>
            {SORTES.map(([cle, libelle, aide]) => (
              <label
                key={cle}
                style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}
              >
                <input
                  type="checkbox"
                  checked={choix[cle]}
                  onChange={(e) => setChoix((c) => ({ ...c, [cle]: e.target.checked }))}
                  style={{ width: 18, height: 18, marginTop: 1 }}
                />
                <span>
                  <b>{tr(libelle)}</b>
                  {aide && <span style={{ color: C.muted }}> — {tr(aide)}</span>}
                </span>
              </label>
            ))}
            <div style={{ margin: '8px 0', color: C.red, fontWeight: 700 }}>
              {tr('⚠ C’est définitif. Télécharge d’abord tes données si tu veux les garder.')}
            </div>
            <div style={{ marginBottom: 8 }}>
              <Btn
                label="💾 Télécharger mes données"
                ghost
                color={C.navy}
                full
                onClick={() => telechargeLaSauvegarde().catch(() => {})}
              />
            </div>
            <Inp
              label={trf('Écris « {0} » pour confirmer', MOT_DE_CONFIRMATION)}
              value={mot}
              placeholder={MOT_DE_CONFIRMATION}
              onChange={(e) => setMot(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Btn label="Annuler" ghost color={C.grey} full onClick={() => setOuvert(false)} />
              </div>
              <div style={{ flex: 1 }}>
                <Btn
                  label={enCours ? '⏳ Effacement…' : '🧹 Tout effacer'}
                  color={C.red}
                  full
                  onClick={pret ? efface : undefined}
                />
              </div>
            </div>
          </div>
        )}
        {erreur && (
          <div role="alert" style={{ fontSize: 11, color: C.red, fontWeight: 700, marginTop: 8 }}>
            ✕ {erreur}
          </div>
        )}
      </div>
    </Card>
  );
}
